import type { RemittanceConfig } from "./types";

let _config: RemittanceConfig | null = null;

/**
 * Initialize the remittance config for non-React code (utilities, API handlers, server code).
 * Call this once at application startup, typically in your root layout or _app file.
 *
 * @example
 * ```ts
 * // In your app's entry point or layout
 * import { initRemittanceConfig } from "cy-remittance-config";
 * import { remittanceConfig } from "@/config/remittance";
 *
 * initRemittanceConfig(remittanceConfig);
 * ```
 */
export function initRemittanceConfig(config: RemittanceConfig): void {
  _config = config;
}

/**
 * Get the current remittance config for non-React code.
 * Throws an error if config hasn't been initialized.
 *
 * @example
 * ```ts
 * import { getRemittanceConfig } from "cy-remittance-config";
 *
 * function validateTransactionLimit(amount: number): boolean {
 *   const config = getRemittanceConfig();
 *   const standardLimit = config.accountLimits.find(l => l.id === "standard");
 *   return standardLimit ? amount <= standardLimit.limits.daily : false;
 * }
 * ```
 */
export function getRemittanceConfig(): RemittanceConfig {
  if (!_config) {
    throw new Error(
      "Remittance config not initialized. " +
        "Call initRemittanceConfig(config) at application startup.",
    );
  }
  return _config;
}

/**
 * Get the current remittance config, returning null if not initialized.
 * Useful for code that needs to work both with and without config.
 */
export function getRemittanceConfigOptional(): RemittanceConfig | null {
  return _config;
}

/**
 * Check if remittance config has been initialized.
 */
export function isRemittanceConfigInitialized(): boolean {
  return _config !== null;
}

/**
 * Reset the config store. Primarily useful for testing.
 */
export function resetRemittanceConfig(): void {
  _config = null;
}
